import { useState, VFC } from 'react'
import { postInterested, postNotInterested } from 'src/api/recommendation'
import { useUser } from 'src/api/user'
import { UserIcon } from 'src/components/Image/UserIcon'
import {
  ProfilePreviewWithButtonModal,
  ProfileViewModal,
} from 'src/components/ProfilePreview'
import { PromotionModal } from 'src/components/PromotionModal'
import { RequestReasonModal } from 'src/components/RequestReasonModal'
import { getActions } from 'src/components/UserCard'
import { Promotion } from 'src/models/Promotion'
import { RecommendedUser } from 'src/models/User'
import { _messageroomborder } from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { IS_DEVELOPMENT } from 'src/utils/env'
import styled from 'styled-components'
import { MessageOverview, RoomInfo, RoomItemDiv } from './RoomItem'

export const PRItem: VFC<{
  data: Promotion
}> = ({ data }) => {
  const [isShowReasonModal, setIsShowReasonModal] = useState(false)
  const [isPRModalOpen, setPRModalOpen] = useState(false)
  const [isProfileModalOpen, setProfileModalOpen] = useState(false)
  const [isOpenProfileModalWithButton, setIsOpenProfileModalWithButton] =
    useState(false)
  const [status, setStatus] = useState<RecommendedUser['matchingStatus']>(null)

  const { id, token, user, content, prDetail, displayType } = data
  const { data: profileUser } = useUser(user.id, {
    dedupingInterval: IS_DEVELOPMENT ? undefined : 300000,
  })
  const onClickInterested = (reason: string | undefined) =>
    postInterested(user.id, reason, {
      id: id,
      token: token,
    }).then(() => {
      setStatus('request')
      setIsShowReasonModal(false)
    })

  const onClickNotInterested = () =>
    postNotInterested(user.id, { id, token }).then(() => {
      setStatus('not_interest')
      setIsOpenProfileModalWithButton(false)
    })

  const actions = getActions(
    { ...profileUser, matchingStatus: status },
    () => onClickNotInterested(),
    () => {
      setIsOpenProfileModalWithButton(false)
      setIsShowReasonModal(true)
    },
  )

  return (
    <>
      <Item
        iconSrc={user.imageSrc}
        companyName={user.companyName}
        name={user.name}
        message={displayType === 'profile' ? content : prDetail}
        openPR={() => setPRModalOpen(true)}
      />
      {isPRModalOpen && (
        <PromotionModal
          status={status}
          promotion={data}
          close={() => setPRModalOpen(false)}
          showDetail={() => setProfileModalOpen(true)}
          showReasonModal={() => {
            setIsShowReasonModal(true)
            setPRModalOpen(false)
          }}
          displayType={displayType}
          prDetail={prDetail || ''}
          showProfileModalWithButton={() => {
            setPRModalOpen(false)
            setIsOpenProfileModalWithButton(true)
          }}
          onClickNotInterested={onClickNotInterested}
          isDisplayedProfileButton
        />
      )}
      {isShowReasonModal && (
        <RequestReasonModal
          postInterested={(reason) => onClickInterested(reason)}
          close={() => setIsShowReasonModal(false)}
        />
      )}
      {isProfileModalOpen && (
        <ProfileViewModal
          id={user.id}
          close={() => setProfileModalOpen(false)}
        />
      )}
      {isOpenProfileModalWithButton && profileUser && (
        <ProfilePreviewWithButtonModal
          user={profileUser}
          close={() => setIsOpenProfileModalWithButton(false)}
          actions={actions}
        />
      )}
    </>
  )
}

type ItemProps = {
  iconSrc: string | undefined
  companyName: string
  name: string
  position?: string | null
  message?: string
  openPR: VoidFunction
}

const Item: VFC<ItemProps> = ({
  iconSrc,
  companyName,
  name,
  position,
  message,
  openPR,
}) => {
  return (
    <PRItemDiv>
      <PRItemButton onClick={openPR}>
        <PRItemButtonDiv>
          <UserIcon src={iconSrc} size="48px" />
          <RoomInfo>
            <p>{companyName}</p>
            <p>{position ? `${name} / ${position}` : name}</p>
            <PRText>{message}</PRText>
          </RoomInfo>
          <aside>
            <PRMark>PR</PRMark>
          </aside>
        </PRItemButtonDiv>
      </PRItemButton>
    </PRItemDiv>
  )
}

const PRItemButton = styled.button`
  width: 100%;
`

const PRItemButtonDiv = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  height: 100%;
`

const PRText = styled(MessageOverview)`
  color: white;
  white-space: pre-wrap;
`

const PRItemDiv = styled(RoomItemDiv)`
  background: linear-gradient(120deg, #02b7b0, #0286b7);
  color: white;
  :hover {
    background: linear-gradient(120deg, #02b7b0, #0286b7) !important;
  }
`
const PRMark = styled.div`
  padding: 4px 12px;
  border: 1px solid ${_messageroomborder};
  border-radius: 5px;
  color: black;
  background-color: white;
  font-weight: ${fontWeightBold};
`
